import dateUtil from '../daterange-constants';

// How long is data considered "up-to-date"? (won't be re-retrieved)
const CACHE_TTL = 1000 * 60;

const DOWNLOAD_STATE = {
  pending: 'pending',
  success: 'success',
  error: 'error',
};

const CACHE = {
    /* example:
    "11112222-3333-4444-5555-666677778888": {
        "air_temp_avg": {
            "timeRangeType": "exact_date_range",
            "timeRangeOptions": {
              "epochStart": 1616600000000,
              "epochEnd": 1616600100000,
            },
            data: [],
            validUntil: 1616600200000,
            state: DOWNLOAD_STATE.pending,
            downloadPromise: Promise.resolve(),
        },
    }
    */
};

let _api;
let _initFinished = false;

/**
 * Initialize module. Setting API externally for unit-test's benefit.
 * 
 * @param {object} api 
 */
export const init = function(api) {
    _api = api;
    _initFinished = true;
};

/**
 * Delete data from the cache if we have passed it's "ValidUntil" time.
 */
const dumpOldCacheData = function() {

    const now = new Date().getTime();

    for (const stationId in CACHE) {

      const cachedMeasurements = Object.keys(CACHE[stationId]);

      for (const measurementName of cachedMeasurements) {
        if (CACHE[stationId][measurementName].validUntil < now) {
          delete CACHE[stationId][measurementName];
        }
      }
    }
};

/**
 * Take a configuration of panel measurements, and transform it into
 * an object grouped by station (API requests target single-station, multi-
 * measurements).
 * 
 * Example output:
 * {
 *   "11112222-3333-4444-5555-666677778888": {
 *     "measurements": {
 *       "air_temp": true,
 *       "dew_point": "true"
 *     },
 *     "panels": {
 *       "1": true,
 *       "5": true
 *     }
 *   }
 * }
 * 
 * @param {object} panelMeasurements 
 */
const groupMeasurementsByStation = function(panelMeasurements) {

    const measurementsByStation = {};

    for (const panel of panelMeasurements) {
        const panelId = panel.panel_id;

        for (const source of panel.sources) {
            for (const stationMeasurements of source) {

                if (stationMeasurements.measurements.length > 0) {

                    if (!measurementsByStation[stationMeasurements.station_id]) {
                        measurementsByStation[stationMeasurements.station_id] = { measurements: {}, panels: {} };
                    }

                    for (const measurementName of stationMeasurements.measurements) {
                        measurementsByStation[stationMeasurements.station_id].measurements[measurementName] = true;
                    }
                    measurementsByStation[stationMeasurements.station_id].panels[panelId] = true;
                }
            }
        }
    }

    return measurementsByStation;
};

/**
 * Delete all cached data that matches the input.
 * 
 * @param {object} measurementsByStation 
 */
const deleteCacheData = function(measurementsByStation) {

    for (const stationId in measurementsByStation) {
        if (CACHE[stationId]) {
            for (const measurementName in measurementsByStation[stationId].measurements) {
                delete CACHE[stationId][measurementName];
            }
        }
    }
};

/**
 * Loop through all the requested measurements. Return all measurements that
 * do not have an up-to-date cache (they will be retrieved from api).
 * 
 * @param {object} measurementsByStation 
 * @param {object} timeRange 
 */
const determineMeasurementsNeedingDownload = function(measurementsByStation, timeRange) {

    const measurementsNeedingDownload = [];

    for (const stationId in measurementsByStation) {

        const measurements = [];

        for (const measurementName in measurementsByStation[stationId].measurements) {
            if (!isMeasurementUpToDate(stationId, measurementName, timeRange)) {
                measurements.push(measurementName);
            }
        }

        if (measurements.length) {
            measurementsNeedingDownload.push({
                station: stationId,
                measurements,
                panels: Object.keys(measurementsByStation[stationId].panels),
            });
        }
    }

    return measurementsNeedingDownload;
};

/**
 * Returns true if the measurement currently has a valid, up-to-date
 * cache (otherwise returns false).
 * 
 * @param {string} stationId 11112222-3333-4444-5555-666677778888
 * @param {string} measurementName air_temp_avg
 * @param {object} timeRange 
 */
const isMeasurementUpToDate = function(stationId, measurementName, timeRange) {

    return !!(CACHE[stationId] && CACHE[stationId][measurementName] &&
        CACHE[stationId][measurementName].timeRangeType === timeRange.type &&
        timeRangeOptionsEqual(CACHE[stationId][measurementName].timeRangeOptions, timeRange.options) &&
        CACHE[stationId][measurementName].state !== DOWNLOAD_STATE.error);
};

/**
 * Compare two time range options object to see if they deeply match.
 * 
 * @param {object} a timeRange.options
 * @param {object} b timeRange.options
 */
const timeRangeOptionsEqual = function(a, b) {

    if (Object.keys(a).length !== Object.keys(b).length) {
        return false;
    }

    for (const field in a) {
        if (a[field] !== b[field]) {
            return false;
        }
    }

    return true;
};

/**
 * Add station IDs to the cache.
 * 
 * @param {array} stationIds 
 */
const addStationsToCache = function(stationIds) {

    for (const id of stationIds) {
        if (!CACHE[id]) {
            CACHE[id] = {};
        }
    }
};

/**
 * Generate a new data cache object.
 * 
 * @param {object} timeRange 
 * @param {number} cacheTTL 
 */
const generateDataCacheObject = function(timeRange, cacheTTL) {

    return {
        timeRangeType: timeRange.type,
        //deep clone
        timeRangeOptions: JSON.parse(JSON.stringify(timeRange.options)),
        data: null,
        validUntil: new Date().getTime() + cacheTTL,
        state: DOWNLOAD_STATE.pending,
        downloadPromise: null,
    };
};

/**
 * Example panelMeasurements:
 * [
 *  {
 *    "panel_id": 2,
 *    "sources": [
 *      [
 *        {
 *          "station_id": "e2a10666-aa79-d6f7-07d6-8a2edf60a861",
 *          "measurements": [
 *            "wind_chill",
 *            "water_pressure_business-focused",
 *            "water_pressure",
 *            "traffic_speed"
 *          ]
 *        }
 *      ],
 *      [
 *        {
 *          "station_id": "7faee08e-2701-60bd-36ba-7276d0fa82f7",
 *          "measurements": [
 *            "surface_temperature"
 *          ]
 *        }
 *      ]
 *    ]
 *  }
 * ]
 * 
 * @param {object} options 
 */
export const getData = function(options) {

    const {
        panelMeasurements,
        stations,
        timeRange,
        force = false,
        cacheTTL = CACHE_TTL,
    } = options;

    if (!_initFinished) throw new Error(`Must init before using data-manager`);

    dumpOldCacheData();

    const measurementsByStation = groupMeasurementsByStation(panelMeasurements);

    if (force === true) {
        deleteCacheData(measurementsByStation);
    }

    const measurementsNeedingDownload = determineMeasurementsNeedingDownload(measurementsByStation, timeRange);

    addStationsToCache(measurementsNeedingDownload.map(e => e.station));

    for (const stationGroup of measurementsNeedingDownload) {

        const cacheMap = {};
        for (const measurement of stationGroup.measurements) {

            CACHE[stationGroup.station][measurement] = generateDataCacheObject(timeRange, cacheTTL);
            cacheMap[measurement] = CACHE[stationGroup.station][measurement];
        }

        const stationEntity = stations.find(e => e.id === stationGroup.station);

        // for the rare case where the station is no longer in the organization
        if (!stationEntity) {
            for (const measurement of stationGroup.measurements) {
                CACHE[stationGroup.station][measurement].downloadPromise = Promise.reject();
                CACHE[stationGroup.station][measurement].state = DOWNLOAD_STATE.error;
            }
        } else {
            
            const legacyId = stationEntity.legacy_id;

            const promise = _api.getMeasurementData(legacyId, stationGroup.measurements, dateUtil.getEpochRange(timeRange))
                .then(result => {
                    for (const measurement in cacheMap) {
                        cacheMap[measurement].data = result.data[measurement] || []; // default to empty array. api sometimes returns nothing
                        cacheMap[measurement].state = DOWNLOAD_STATE.success;
    
                        const measurementDefinition = stationEntity.sensor_definitions.find(e => e.name === measurement);
                        // stringify arrays-type measurements. They are rarely used
                        if (measurementDefinition && measurementDefinition.type === 'array') {
                            for (let row of cacheMap[measurement].data) {
                                row[1] = JSON.stringify(row[1]);
                            }
                        }
                    }
                }).catch(err => {
                    for (const measurement in cacheMap) {
                        cacheMap[measurement].state = DOWNLOAD_STATE.error;
                    }
                    throw err;
                });
    
            for (const measurement in cacheMap) {
                cacheMap[measurement].downloadPromise = promise;
            }
        }
    }

    const panelPromises = {};

    for (const panel of panelMeasurements) {

        const panelId = panel.panel_id;
        const promises = [];
        const alreadyHandled = {};

        for (const source of panel.sources) {
            for (const stationGroup of source) {

                if (!alreadyHandled[stationGroup.station_id]) {
                    alreadyHandled[stationGroup.station_id] = {};
                }

                for (const measurement of stationGroup.measurements) {

                    if (!alreadyHandled[stationGroup.station_id][measurement]) {

                        alreadyHandled[stationGroup.station_id][measurement] = true;

                        // hold reference in case the cache is overwritten by a newer request
                        const cacheByReference = CACHE[stationGroup.station_id][measurement];

                        promises.push(cacheByReference.downloadPromise.then(result => {
                            return {
                                station: stationGroup.station_id,
                                measurement,
                                data: cacheByReference.data,
                            };
                        }));
                    }
                }
            }
        }

        panelPromises[panelId] = Promise.all(promises);
    }

    return panelPromises;
};

export default getData;

// for unit-tests only
// module.exports = {
//     timeRangeOptionsEqual,
//     CACHE,
//     isMeasurementUpToDate,
//     init,
//     determineMeasurementsNeedingDownload,
//     deleteCacheData,
//     groupMeasurementsByStation,
//     dumpOldCacheData,
//     getData,
//     addStationsToCache,
//     generateDataCacheObject,
// };